import {
  Col,
  ENotificationType,
  Row,
  TRtkErrorType,
  useGetValidationData,
  useShowToastNotification,
  useTranslation,
} from '@packages/utils';
import { useTheme } from 'styled-components';
import EditableInput from 'components/Base/EditableFields/EditableInput';
import { InplaceEditLabels } from 'components/Base/EditableFields/components/InplaceEditLabel';
import { useUpdatePeopleEmailMutation } from 'redux/apiSlice/people.slice';
import { Icon, EIconNames } from 'components/Base/Icon';
import useCopyToClipboard from 'hooks/useCopyClipboard';
import { TPeopleEmailProps } from './PeopleEmail.types';

const PeopleEmail = ({ userId, data }: TPeopleEmailProps) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.people.details.details-tab.main-contact.email',
  });
  const theme = useTheme();
  const [isCopied, copyToClipboard] = useCopyToClipboard();
  const { validationRules } = useGetValidationData();
  const { showToastNotification } = useShowToastNotification();
  const [editEmail, { isLoading, isError }] = useUpdatePeopleEmailMutation();

  const onEdit = async (value: string) => {
    try {
      await editEmail({
        userId,
        email: value,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleCopyClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    copyToClipboard(data.value || '');
  };

  return (
    <Row align="middle" wrap={false} className="w-100 mb-1">
      <Col>
        <InplaceEditLabels
          label={t('INPLACE_EDIT_LABEL')}
          icon={EIconNames.EMAIL_OUTLINED}
        />
      </Col>
      <Col flex="auto">
        <EditableInput
          onEdit={onEdit}
          title={data.value}
          isError={isError}
          isLoading={isLoading}
          fitContent={false}
          validateTrigger="onSubmit"
          rules={[validationRules.email()]}
          readonly={data?.allowedOperations.readonly}
          canRemove={data?.allowedOperations.canRemove}
          emptyText={t('EMPTY_TEXT')}
          placeholder={t('PLACEHOLDER')}
          backIcon
        />
      </Col>
      {data.value && (
        <Col onClick={handleCopyClick}>
          <Icon
            icon={isCopied ? EIconNames.CHECK : EIconNames.COPY_OUTLINED}
            color={theme.colorBlackIconColor}
            className="pointer"
            size={16}
          />
        </Col>
      )}
    </Row>
  );
};

export default PeopleEmail;
